/* eslint-disable react/no-did-mount-set-state */
/**
 * @module UserSettings
 * Container Component UserSettings
 */

import { EYForm, FormComboBox, FormStaticText, FormTextInput } from '@ey/form';
import ContentSwitcher from '@ey/motif-react-wrapper/ContentSwitcher';
import Button from '@ey/motif-react-wrapper/Button';
import React, { useEffect, useState } from 'react';
import {
	createUserSettings,
	getUserSettings,
	setUserSettings
} from '../../actions/usersettingsaction';
import {
	currentResource,
	dateFormatsListItems,
	displayLanguageListItems,
	labels,
	negativeNumberFormatsListItems,
	numberFormatsListItems,
	pagerOptions,
	preferredLandingPageFilterSwitcher,
	preferredLandingPageViewSwitcher,
	preferredThemeSwitcher,
	setDateFormat,
	setLanguage,
	settingsPreferences,
	standardStrings,
	urls
} from '../../util/uiconstants';
import { useDispatch, useSelector } from 'react-redux';

import env from '../../util/env';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';

export const PHONE_VALIDATION_REGEX = /^(?=.*\d)[\d\s+\-().]+$/;

const UserSettings = (props) => {
	// Params
	const dispatch = useDispatch();
	const userSettings = useSelector((state) => state.userSettings);
	const history = useHistory();
	const roleList = currentResource.rolelistitems;
	const userSettingsUrl = env.getURL('serviceUrl') + '/' + urls.USERS_URL;
	//State
	const [isLoading, setIsLoading] = useState(true);
	const [userId, setUserId] = useState(
		userSettings.id ? userSettings.id : standardStrings.EMPTY
	);
	const [userfname, setUserfname] = useState(
		userSettings.data.userfname
			? userSettings.data.userfname
			: standardStrings.EMPTY
	);
	const [userlname, setUserlname] = useState(
		userSettings.data.userlname
			? userSettings.data.userlname
			: standardStrings.EMPTY
	);
	const [userinitials, setUserinitials] = useState(
		userSettings.data.userinitials
			? userSettings.data.userinitials
			: standardStrings.EMPTY
	);
	const [userprimaryoffice, setUserprimaryoffice] = useState(
		userSettings.data.userprimaryoffice
			? userSettings.data.userprimaryoffice
			: standardStrings.EMPTY
	);
	const [userpreferredcontactphone, setUserpreferredcontactphone] = useState(
		userSettings.data.userpreferredcontactphone
			? userSettings.data.userpreferredcontactphone
			: standardStrings.EMPTY
	);
	const [userroleid, setUserroleid] = useState(
		userSettings.data.userroleid ? userSettings.data.userroleid : null
	);
	const [userlanguageid, setUserlanguageid] = useState(
		userSettings.data.userlanguageid && userSettings.data.userlanguageid > 0
			? userSettings.data.userlanguageid
			: 1
	);
	const [userdatetimeformat, setUserdatetimeformat] = useState(
		userSettings.data.userdatetimeformat &&
			userSettings.data.userdatetimeformat > -1
			? userSettings.data.userdatetimeformat
			: 0
	);
	const [usernumberformat, setUsernumberformat] = useState(
		userSettings.data.usernumberformat &&
			userSettings.data.usernumberformat > -1
			? userSettings.data.usernumberformat
			: 0
	);
	const [usernegativenumberformat, setUsernegativenumberformat] = useState(
		userSettings.data.usernegativenumberformat &&
			userSettings.data.usernegativenumberformat > -1
			? userSettings.data.usernegativenumberformat
			: 0
	);
	const [validationTriggerCounter, setValidationTriggerCounter] = useState(0);
	const [preferredlandingpagefilter, setPreferredlandingpagefilter] = useState(
		userSettings.data.preferredlandingpagefilter
			? userSettings.data.preferredlandingpagefilter
			: ''
	);
	const [preferredlandingpageview, setPreferredlandingpageview] = useState(
		userSettings.data.preferredlandingpageview
			? userSettings.data.preferredlandingpageview
			: ''
	);
	const [preferrednumberofengagements, setPreferrednumberofengagements] =
		useState(
			userSettings.data.preferrednumberofengagements
				? userSettings.data.preferrednumberofengagements
				: null
		);
	const [preferredtheme, setPreferredtheme] = useState(
		userSettings.data.preferredtheme ? userSettings.data.preferredtheme : ''
	);

	const [activeIndexPrefLandingPageView, setActiveIndexPrefLandingPageView] =
		useState(
			userSettings.data.preferredlandingpageview &&
				userSettings.data.preferredlandingpageview ===
				settingsPreferences.WorkspaceList
				? preferredLandingPageViewSwitcher.WorkspaceList
				: preferredLandingPageViewSwitcher.EngagementList
		);
	const [
		activeIndexPrefLandingPageFilter,
		setActiveIndexPrefLandingPageFilter
	] = useState(
		userSettings.data.preferredlandingpagefilter &&
			userSettings.data.preferredlandingpagefilter ===
			settingsPreferences.FavoriteEngagements
			? preferredLandingPageFilterSwitcher.FavoriteEngagements
			: preferredLandingPageFilterSwitcher.AllEngagements
	);
	const [activeIndexPrefTheme, setActiveIndexPrefTheme] = useState(
		userSettings.data.preferredtheme &&
			userSettings.data.preferredtheme === settingsPreferences.DarkTheme
			? preferredThemeSwitcher.Dark
			: preferredThemeSwitcher.Light
	);

	useEffect(() => {
		setIsLoading(true);
		const loadUserSettings = async () => {
			await dispatch(getUserSettings(userSettingsUrl)).finally(() =>
				setIsLoading(false)
			);
		};
		loadUserSettings();
	}, []);

	const isFirstTimeUser = () => {
		const _isFirstTimeUser = !(
			userSettings.data &&
			userSettings.data.id &&
			userSettings.data.id !== '00000000-0000-0000-0000-000000000000' &&
			userSettings.data.userlanguageid > 0 &&
			userSettings.data.userdatetimeformat > -1 &&
			userSettings.data.usernumberformat > -1
		);
		return _isFirstTimeUser;
	};

	// Guest user does not have certain option available on the
	// UI, so hide it.
	const isGuestUser = () => {
		if (
			userSettings.data &&
			userSettings.data.isguestuser !== null &&
			userSettings.data.isguestuser !== undefined
		) {
			return userSettings.data.isguestuser;
		}

		return false;
	};

	const saveUserDetails = async () => {
		setIsLoading(true);

		let model = {
			collections: {
				userscollection: [
					{
						data: {
							userpreferredcontactphone: userpreferredcontactphone,
							userinitials: userinitials,
							userroleid: userroleid,
							userlanguageid: userlanguageid,
							userfname: userfname,
							userlname: userlname,
							userprimaryoffice: userprimaryoffice,
							userdatetimeformat: userdatetimeformat,
							usernumberformat: usernumberformat,
							usernegativenumberformat: usernegativenumberformat,
							preferredlandingpageview: preferredlandingpageview,
							preferredlandingpagefilter: preferredlandingpagefilter,
							preferrednumberofengagements: preferrednumberofengagements,
							preferredtheme: preferredtheme
						}
					}
				]
			}
		};

		// Existing User
		if (userId && userId !== '00000000-0000-0000-0000-000000000000') {
			await dispatch(setUserSettings(userSettingsUrl + '/' + userId, model));
		} else {
			// New User
			await dispatch(createUserSettings(userSettingsUrl, model));
		}
	};

	const isFormValid = () => {
		const phoneValue = (userpreferredcontactphone || standardStrings.EMPTY).trim();
		const isValid =
			(userfname || standardStrings.EMPTY).length > 0 &&
			(userlname || standardStrings.EMPTY).length > 0 &&
			(userinitials || standardStrings.EMPTY).length > 0 &&
			(userprimaryoffice || standardStrings.EMPTY).length > 0 &&
			phoneValue.length > 0 &&
			PHONE_VALIDATION_REGEX.test(phoneValue) &&
			!Number.isNaN(Number.parseInt(userroleid)) &&
			!Number.isNaN(Number.parseInt(userlanguageid)) &&
			!Number.isNaN(Number.parseInt(userdatetimeformat)) &&
			!Number.isNaN(Number.parseInt(usernumberformat)) &&
			!Number.isNaN(Number.parseInt(usernegativenumberformat));

		return isValid;
	};

	const handleSave = async () => {
		if (isFormValid()) {
			await saveUserDetails();

			// update redux state
			await dispatch(getUserSettings(userSettingsUrl));

			setLanguage(userlanguageid);
			setDateFormat(userdatetimeformat);
			setIsLoading(false);
			redirectToPreferredLandingPage();
		} else {
			setValidationTriggerCounter(validationTriggerCounter + 1);
		}
	};

	const handleCancel = () => {
		history.goBack();
	};

	const handleActiveClassPrefLandingPageView = (index) => {
		return activeIndexPrefLandingPageView === index ? 'motif-active' : '';
	};

	const handleActiveClassPrefLandingPageFilter = (index) => {
		return activeIndexPrefLandingPageFilter === index ? 'motif-active' : '';
	};

	const handleActiveClassPrefTheme = (index) => {
		return activeIndexPrefTheme === index ? 'motif-active' : '';
	};

	const handlePrefLandingPageViewClick = (event, indexSwitcher) => {
		setActiveIndexPrefLandingPageView(indexSwitcher);

		if (indexSwitcher === preferredLandingPageViewSwitcher.WorkspaceList)
			setPreferredlandingpageview(settingsPreferences.WorkspaceList);
		else setPreferredlandingpageview(settingsPreferences.EngagementList);

		event.preventDefault();
	};

	const handlePrefLandingPageFilterClick = (event, indexSwitcher) => {
		setActiveIndexPrefLandingPageFilter(indexSwitcher);

		if (
			indexSwitcher === preferredLandingPageFilterSwitcher.FavoriteEngagements
		)
			setPreferredlandingpagefilter(settingsPreferences.FavoriteEngagements);
		else setPreferredlandingpagefilter(settingsPreferences.AllEngagements);

		event.preventDefault();
	};

	const handlePrefThemeClick = (event, indexSwitcher) => {
		setActiveIndexPrefTheme(indexSwitcher);

		if (indexSwitcher === preferredThemeSwitcher.Dark)
			setPreferredtheme(settingsPreferences.DarkTheme);
		else setPreferredtheme(settingsPreferences.LightTheme);

		event.preventDefault();
	};

	const redirectToPreferredLandingPage = () => {
		let path = '',
			searchPath = '';

		preferredlandingpagefilter === settingsPreferences.AllEngagements
			? (path = '/engagements')
			: (path = '/favorites');

		preferredlandingpageview === settingsPreferences.EngagementList
			? (searchPath = '')
			: (searchPath = '?groupby=clients');

		history.push({
			pathname: path,
			search: searchPath
		});
	};

	const handleInputChange = (key, value) => {
		switch (key) {
			case 'userfname':
				setUserfname(value);
				break;
			case 'userlname':
				setUserlname(value);
				break;
			case 'userinitials':
				setUserinitials(value);
				break;
			case 'userprimaryoffice':
				setUserprimaryoffice(value);
				break;
			case 'userroleid':
				setUserroleid(value);
				break;
			case 'userpreferredcontactphone':
				setUserpreferredcontactphone(value);
				break;
			case 'userlanguageid':
				setUserlanguageid(value);
				break;
			case 'userdatetimeformat':
				setUserdatetimeformat(value);
				break;
			case 'usernumberformat':
				setUsernumberformat(value);
				break;
			case 'usernegativenumberformat':
				setUsernegativenumberformat(value);
				break;
			case 'preferrednumberofengagements':
				setPreferrednumberofengagements(value);
				break;
		}
	};

	const GetUserLanguages = () => {
		let collection = [];

		// check if russia is enabled
		// enabled => show Russia language in user option
		// Disabled => If user has current russai selection keep it
		// otherwise remove it
		Object.keys(displayLanguageListItems).map((displayLanguageId) => {
			if (
				displayLanguageId != 13 ||
				env.getRussiaLanguageEnabled() ||
				(displayLanguageId == 13 && userlanguageid == 13)
			) {
				collection.push({
					value: String(displayLanguageId),
					label: displayLanguageListItems[displayLanguageId]
				});
			}
		});

		return collection;
	};

	return (
		<>
			{isLoading ? (
				<section className="loader-wrapper">
					<LoadingIndicator show fullscreen />
				</section>
			) : !isLoading && userSettings && userSettings.data ? (
				<UserSettingsWrapper className="customScrollbar">
					<header>
						<h5>{labels.usersettings}</h5>
					</header>
					<form className="userfields">
						<EYForm
							id="userSettingsForm"
							onChange={handleInputChange}
							onSubmit={(e) => {
								e.preventDefault();
							}}
							action="\"
							validationTriggerCounter={validationTriggerCounter}
						>
							<section className="form-title">
								<FormStaticText label={labels.engagementsettings}>
									{labels.usersettingsnewengagements}
								</FormStaticText>
							</section>
							<FormTextInput
								placeholder={labels.enterfirstname}
								name="userfname"
								value={userfname}
								rules={[]}
								is_required
								help
								maxLength="255"
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.firstname
								)}
							/>
							<FormTextInput
								placeholder={labels.enterlastname}
								name="userlname"
								value={userlname}
								rules={[]}
								is_required
								help
								maxLength="255"
								errormessage={labels.isRequired.replace('{0}', labels.lastname)}
							/>
							<FormTextInput
								placeholder={labels.signoffinitials}
								name="userinitials"
								value={userinitials}
								rules={[]}
								is_required
								help
								maxLength="3"
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.signoffinitials
								)}
							/>
							<FormTextInput
								placeholder={labels.primaryoffice}
								name="userprimaryoffice"
								value={userprimaryoffice}
								rules={[]}
								is_required
								help
								maxLength="255"
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.primaryoffice
								)}
							/>
							<FormComboBox
								id="userroleid"
								name="userroleid"
								value={String(userroleid)}
								is_required
								help
								placeholder={labels.roles}
								options={roleList.map((role) => {
									return {
										value: String(role.id),
										label: role.label
									};
								})}
								errormessage={labels.isRequired.replace('{0}', labels.roles)}
								clearable={false}
							/>
							<FormTextInput
								placeholder={labels.phonenumber}
								name="userpreferredcontactphone"
								value={userpreferredcontactphone}
								rules={[
									{
										validation: (value) =>
											PHONE_VALIDATION_REGEX.test(value.trim()),
										message: labels.invalidPhoneNumber
									}
								]}
								is_required
								help
								maxLength="20"
								errormessage={
									!userpreferredcontactphone || userpreferredcontactphone.trim().length === 0
										? labels.isRequired.replace('{0}', labels.phonenumber)
										: labels.invalidPhoneNumber
								}
							/>
							<section className="form-title">
								<FormStaticText label={labels.theme} />
							</section>
							<label className="usersettings-label">
								{labels.chooseDisplayTheme}
							</label>
							<ContentSwitcher className="pref-theme">
								<Button
									title={labels.light}
									onClick={(e) => {
										handlePrefThemeClick(e, preferredThemeSwitcher.Light);
									}}
									className={handleActiveClassPrefTheme(
										preferredThemeSwitcher.Light
									)}
								>
									{labels.light}
								</Button>
								<Button
									title={labels.dark}
									onClick={(e) => {
										handlePrefThemeClick(e, preferredThemeSwitcher.Dark);
									}}
									className={handleActiveClassPrefTheme(
										preferredThemeSwitcher.Dark
									)}
								>
									{labels.dark}
								</Button>
							</ContentSwitcher>
							<section className="form-title">
								<FormStaticText label={labels.globalapplicationsettings}>
									{labels.usersettingsallengagements}
								</FormStaticText>
							</section>
							<FormComboBox
								id="userlanguageid"
								name="userlanguageid"
								value={String(userlanguageid)}
								is_required
								help
								placeholder={labels.displaylanguage}
								options={GetUserLanguages()}
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.displaylanguage
								)}
								clearable={false}
							/>
							<FormComboBox
								id="userdatetimeformat"
								name="userdatetimeformat"
								value={String(userdatetimeformat)}
								is_required
								help
								placeholder={labels.preferreddateformat}
								options={dateFormatsListItems.map((dateFormat) => {
									return {
										value: String(dateFormat.id),
										label: dateFormat.label
									};
								})}
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.preferreddateformat
								)}
								clearable={false}
							/>
							<FormComboBox
								id="usernumberformat"
								name="usernumberformat"
								value={String(usernumberformat)}
								is_required
								help
								placeholder={labels.preferrednumberformat}
								options={numberFormatsListItems.map((numberFormat) => {
									return {
										value: String(numberFormat.id),
										label: numberFormat.label
									};
								})}
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.preferrednumberformat
								)}
								clearable={false}
							/>
							<FormComboBox
								id="usernegativenumberformat"
								name="usernegativenumberformat"
								value={String(usernegativenumberformat || 0)}
								is_required
								help
								placeholder={labels.preferrednegativenumberformat}
								options={negativeNumberFormatsListItems.map((numberFormat) => {
									return {
										value: String(numberFormat.id),
										label: numberFormat.label
									};
								})}
								errormessage={labels.isRequired.replace(
									'{0}',
									labels.preferrednegativenumberformat
								)}
								clearable={false}
							/>
							<label className="usersettings-label">
								{labels.preferredlandingpageview}
							</label>
							<ContentSwitcher className="prefland-pageview">
								<Button
									title={labels.engagementlist}
									onClick={(e) => {
										handlePrefLandingPageViewClick(
											e,
											preferredLandingPageViewSwitcher.EngagementList
										);
									}}
									className={handleActiveClassPrefLandingPageView(
										preferredLandingPageViewSwitcher.EngagementList
									)}
								>
									{labels.engagementlist}
								</Button>
								<Button
									title={labels.workspacelistusersettings}
									onClick={(e) => {
										handlePrefLandingPageViewClick(
											e,
											preferredLandingPageViewSwitcher.WorkspaceList
										);
									}}
									className={handleActiveClassPrefLandingPageView(
										preferredLandingPageViewSwitcher.WorkspaceList
									)}
								>
									{labels.workspacelistusersettings}
								</Button>
							</ContentSwitcher>

							{!isGuestUser() ? (
								<React.Fragment>
									<label className="usersettings-label">
										{labels.preferredlandingpagefilter}
									</label>
									<ContentSwitcher className="prefland-pagefilter">
										<Button
											title={labels.allengagements}
											onClick={(e) => {
												handlePrefLandingPageFilterClick(
													e,
													preferredLandingPageFilterSwitcher.AllEngagements
												);
											}}
											className={handleActiveClassPrefLandingPageFilter(
												preferredLandingPageFilterSwitcher.AllEngagements
											)}
										>
											{labels.allengagements}
										</Button>
										<Button
											title={labels.favoriteengagements}
											onClick={(e) => {
												handlePrefLandingPageFilterClick(
													e,
													preferredLandingPageFilterSwitcher.FavoriteEngagements
												);
											}}
											className={handleActiveClassPrefLandingPageFilter(
												preferredLandingPageFilterSwitcher.FavoriteEngagements
											)}
										>
											{labels.favoriteengagements}
										</Button>
									</ContentSwitcher>
								</React.Fragment>
							) : (
								<div />
							)}

							<label className="usersettings-label engnum-select">
								{labels.engagementsclientstoload}
							</label>
							{
								<FormComboBox
									id="preferrednumberofengagements"
									className="pref-numeng"
									name="preferrednumberofengagements"
									value={String(preferrednumberofengagements)}
									is_required
									help
									placeholder=""
									options={pagerOptions.map((pagerOption) => {
										return {
											value: String(pagerOption),
											label: pagerOption
										};
									})}
									errormessage={labels.isRequired.replace(
										'{0}',
										labels.preferrednumberofengagements
									)}
									clearable={false}
								/>
							}

							<section className="usersettingbtn">
								<Button
									variant="primary"
									type="button"
									value="save"
									onClick={handleSave}
									name="formSaveButton"
								>
									{labels.save}
								</Button>

								{isFirstTimeUser() ? (
									<div />
								) : (
									<Button
										variant="secondary"
										type="button"
										value="cancel"
										onClick={handleCancel}
										name="formCancelButton"
									>
										{labels.cancel}
									</Button>
								)}
							</section>
						</EYForm>
					</form>
				</UserSettingsWrapper>
			) : null}
		</>
	);
};

export default UserSettings;

const UserSettingsWrapper = styled.section`
	height: calc(100vh - var(--px-65));
	display: flex;
	flex: 1;
	flex-direction: column;
	overflow: auto;
	& .loader {
		display: flex;
		align-items: center;
		margin: 0 50%;
	}

	& > header {
		width: 100%;

		& h5 {
			font-size: var(--px-20);
			font-weight: bold;
			margin: var(--px-11) auto;
			width: 100%;
			padding: 0 0 0 var(--px-40);
			max-width: 2048px;
		}
	}

	& .usersettingbtn {
		display: flex;
		padding: var(--px-20) 0;
		margin-left: calc(var(--px-40) * -1);
		margin-right: calc(var(--px-40) * -1);
		padding-left: var(--px-40);
		border-top: var(--px-1) solid var(--neutrals-900);
		& button {
			margin: 0 var(--px-8) 0 0;
		}
	}

	& .userfields {
		margin: 0 auto;
		max-width: 2048px;
		width: 100%;
		padding: var(--px-20) var(--px-40) 0 var(--px-40);
		height: 100vh;
		.engnum-select + .form-group {
			//margin-bottom: var(--px-50);
			@media screen and (max-width: 1366px) {
				//	margin-bottom: var(--px-90);
			}
		}
		& .form-title {
			border-bottom: var(--px-1) solid var(--neutrals-900);
			display: inline-block;
			width: 100%;
			margin-bottom: var(--px-30);

			& .form-group {
				margin: 0;
				padding: 0;
				display: inline;
			}

			& .form-group label.control-label {
				display: inline;
				vertical-align: top;
				color: var(--neutrals-900);
			}

			& > .form-group * {
				font-size: var(--px-16);
				line-height: normal;
				padding: 0;
				margin: 0;
			}

			& .form-control-static {
				float: right;
				line-height: normal;
				font-size: var(--px-14);
				min-height: auto;
			}
		}

		& div.form-group:nth-child(2) {
			width: auto;
		}

		& div.form-group:nth-child(3) {
			width: auto;
			margin-left: var(--px-20);
		}

		& div.form-group {
			display: inline-flex;
			width: 100%;
			font-size: var(--px-14);
			height: var(--px-81);
			margin: 0 0 var(--px-2) 0;
			@media screen and (max-width: 1366px) {
				margin: 0 0 var(--px-15) 0;
			}

			& .input-group .input-label {
				line-height: normal;
			}

			// & label.control-label {
			// 	width: 20%;
			// 	display: inline-block;
			// 	float: left;
			// 	padding: 0;
			// 	font-weight: bold;
			// 	line-height: var(--px-48);
			// }

			& span.input-group {
				width: var(--px-400);
				float: left;

				& input.form-control {
					margin: 0 var(--px-20)0;
					vertical-align: middle;
					border: var(--px-1) solid var(--neutrals-300);
					font-size: var(--px-14);
				}
			}

			& > section {
				width: var(--px-400);
				float: left;

				& .select-label {
					line-height: normal;
					color: var(--neutrals-900);
				}

				& .Select-control {
					margin: 0 var(--px-20) 0 0;
					vertical-align: middle;
					border: var(--px-1) solid var(--neutrals-300);
					line-height: var(--px-46);
				}
			}
			.Select-input {
				background-color: var(--form-element-bg-color) !important;
			}
			.Select-arrow {
				border-color: var(--neutrals-900) !important;
			}
			& .select-value {
				width: var(--px-400);
				float: left;
				.Select-value-label {
					color: var(--neutrals-900) !important;
				}
				& .Select-control {
					border: var(--px-1) solid var(--neutrals-300);
					background-color: var(--form-element-bg-color) !important;
				}
			}
		}
	}
	.usersettings-label {
		font-size: var(--px-14);
		font-weight: normal;
	}
	.prefland-pageview,
	.prefland-pagefilter,
	.pref-theme {
		margin-bottom: var(--px-33);
		button {
			font-size: var(--px-14);
			border-radius: 0;
			min-width: auto;
			padding: var(--px-3) var(--px-30);
			display: block;
			max-width: var(--px-250);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
		&.prefdashboard-settings {
			margin-bottom: var(--px-48);
		}
	}
	.pref-numeng {
		.Select-control {
			.Select-value-label {
				margin-top: 5px;
			}
			.Select-arrow-zone {
				.Select-arrow {
					top: -3px;
					@media screen and (max-width: 1366px) {
						top: -2px;
					}
				}
			}
		}
	}
`;
